import React, { useState, useEffect, useRef } from "react";
import { apiClient } from "../apiClient";
import SignatureCanvas from "react-signature-canvas";

export default function DriverDeliveryRoute({ driverId, driverName }) {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [expandedClient, setExpandedClient] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paymentData, setPaymentData] = useState({ amount: "", method: "cash" });
  const sigCanvas = useRef(null);

  useEffect(() => {
    fetchAll();
  }, [driverId]);

  const fetchAll = async () => {
    if (!driverId) {
      setError("Driver ID missing");
      setLoading(false);
      return;
    }

    try {
      // Fetch driver's delivery plans
      const plans = await apiClient.get("/delivery-plans/me");
      const planIds = plans.map(plan => plan.id);

      // Fetch items for all plans
      let allItems = [];
      for (const planId of planIds) {
        // FIX: Await the request and store the result
        const itemsResponse = await apiClient.get(`/delivery-items/?delivery_plan_id=${planId}`);

        // Add plan date to each item
        const plan = plans.find(p => p.id === planId);
        // FIX: Map over the fetched items (itemsResponse), not the stale state (items)
        const itemsWithDate = itemsResponse.map(item => ({ ...item, planDate: plan?.date }));
        allItems = allItems.concat(itemsWithDate);
      }

      const clientsData = await apiClient.get("/clients/");
      setItems(allItems);
      setClients(clientsData);
    } catch (err) {
      setError("Failed to load route");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedClientId !== null) {
      setSignature(null);
      if (sigCanvas.current) {
        sigCanvas.current.clear();
      }
    }
  }, [selectedClientId]);

  // Group items by date, then by client
  const itemsByDate = items.reduce((acc, item) => {
    const date = item.planDate || "Unknown";
    if (!acc[date]) acc[date] = {};
    if (!acc[date][item.client_id]) acc[date][item.client_id] = [];
    acc[date][item.client_id].push(item);
    return acc;
  }, {});

  // Get items for selected date
  const selectedDateItems = itemsByDate[selectedDate] || {};
  const availableDates = Object.keys(itemsByDate).sort().reverse();

  const handleMarkPaid = async (clientId, clientItems) => {
    // Issue 3 fix: synchronous guard before any await — blocks double-click/double-tap
    if (saving) return;

    const amountReceivedGroszy = Math.round((parseFloat(paymentData.amount) || 0) * 100);

    // Issue 1 fix: cap against the REMAINING balance across all items, not the full total due
    const totalDue = clientItems.reduce((sum, i) => sum + i.total_value_groszy, 0);
    const totalAlreadyPaid = clientItems.reduce((sum, i) => sum + (i.payment_amount_groszy || 0), 0);
    const remainingBalance = totalDue - totalAlreadyPaid;

    if (amountReceivedGroszy <= 0 || amountReceivedGroszy > remainingBalance) {
      alert(`Enter a valid amount (0.01 to ${(remainingBalance / 100).toFixed(2)} PLN remaining)`);
      return;
    }
    if (!signature) {
      alert("Please sign first");
      return;
    }

    setSaving(true);
    try {
      // Issue 2 fix: split the entered amount across every unpaid/partial item
      // for this client, oldest-first, instead of only ever paying clientItems[0].
      let remainingToApply = amountReceivedGroszy;

      for (const item of clientItems) {
        if (remainingToApply <= 0) break;

        const itemOwed = item.total_value_groszy - (item.payment_amount_groszy || 0);
        if (itemOwed <= 0) continue; // already fully paid, skip

        const amountForThisItem = Math.min(itemOwed, remainingToApply);

        // Additive, audit-logged endpoint (Finding #2/#3) — never overwrites
        await apiClient.post(`/delivery-payments/${item.delivery_plan_id}`, {
          delivery_item_id: item.id,
          amount: amountForThisItem,
          notes: null,
          income_id: null,
          payment_method: paymentData.method
        });

        remainingToApply -= amountForThisItem;
      }

      // Signature saved separately, sending ONLY the signature field — never the
      // stale whole object — so there is nothing for the PUT handler to overwrite
      // (belt-and-suspenders on Issue 4, on top of the Step 5 backend guard).
      const primaryItem = clientItems[0];
      await apiClient.put(`/delivery-items/${primaryItem.id}/signature`, { signature: signature });

      await fetchAll();
      setSelectedClientId(null);
      setSignature(null);
      setPaymentData({ amount: "", method: "cash" });
      alert("✅ Payment saved!");
    } catch (err) {
      alert(`❌ ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const clearSignature = () => {
    if (sigCanvas.current) sigCanvas.current.clear();
    setSignature(null);
  };

  const saveSignature = () => {
    if (sigCanvas.current) {
      const canvas = sigCanvas.current.getCanvas();
      if (canvas) {
        const trimmed = trimCanvas(canvas);
        const data = trimmed.toDataURL("image/png");
        setSignature(data);
      }
    }
  };

  const trimCanvas = (sourceCanvas) => {
    const ctx = sourceCanvas.getContext('2d');
    const width = sourceCanvas.width;
    const height = sourceCanvas.height;
    const pixels = ctx.getImageData(0, 0, width, height);
    const data = pixels.data;

    let top = height, bottom = 0, left = width, right = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alphaIndex = (y * width + x) * 4 + 3;
        if (data[alphaIndex] > 0) {
          top = Math.min(top, y);
          bottom = Math.max(bottom, y);
          left = Math.min(left, x);
          right = Math.max(right, x);
        }
      }
    }

    if (top > bottom || left > right) {
      const empty = document.createElement('canvas');
      empty.width = 1;
      empty.height = 1;
      return empty;
    }

    const trimmedWidth = right - left + 1;
    const trimmedHeight = bottom - top + 1;
    const trimmedCanvas = document.createElement('canvas');
    trimmedCanvas.width = trimmedWidth;
    trimmedCanvas.height = trimmedHeight;
    const trimmedCtx = trimmedCanvas.getContext('2d');
    trimmedCtx.drawImage(
      sourceCanvas,
      left, top, trimmedWidth, trimmedHeight,
      0, 0, trimmedWidth, trimmedHeight
    );

    return trimmedCanvas;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5E9D8] to-[#E5D9C8]">
        <div className="max-w-4xl mx-auto p-4">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-pulse">
              <div className="text-[#2D2F5E] text-lg">Loading your delivery route...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5E9D8] to-[#E5D9C8]">
        <div className="max-w-4xl mx-auto p-4">
          <div className="bg-red-50 border-2 border-red-300 rounded-xl shadow-lg p-8 text-center">
            <div className="text-red-600 text-lg">❌ {error}</div>
          </div>
        </div>
      </div>
    );
  }

  const totalDeliveries = Object.keys(selectedDateItems).length;
  const completedDeliveries = Object.values(selectedDateItems).filter(clientItems => {
    const totalDue = clientItems.reduce((sum, i) => sum + i.total_value_groszy, 0);
    const totalPaid = clientItems.reduce((sum, i) => sum + (i.payment_amount_groszy || 0), 0);
    return totalPaid >= totalDue;
  }).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5E9D8] to-[#E5D9C8]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2D2F5E] to-[#3D3F6E] text-white shadow-lg">
        <div className="max-w-4xl mx-auto p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">🚚 Delivery Route</h1>
              <p className="text-sm opacity-90">Driver: {driverName}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{completedDeliveries}/{totalDeliveries}</div>
              <div className="text-xs opacity-90">Completed</div>
            </div>
          </div>

          {/* Date Selector */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {availableDates.map(date => (
              <button
                key={date}
                onClick={() => setSelectedDate(date)}
                className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition ${
                  selectedDate === date
                    ? 'bg-[#F76B4C] text-white shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                📅 {date}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Payment Modal */}
        {selectedClientId !== null && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-[#F76B4C] to-[#E65A3C] text-white p-4 rounded-t-xl">
                <h3 className="font-bold text-lg">💰 Record Payment</h3>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#2D2F5E] mb-2">Amount Received (PLN)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    className="w-full p-3 border-2 border-[#2D2F5E] border-opacity-30 rounded-lg text-[#2D2F5E] bg-white focus:border-[#F76B4C] focus:outline-none text-lg"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D2F5E] mb-2">Customer Signature</label>
                  <div className="border-2 border-[#2D2F5E] border-opacity-20 rounded-lg bg-white p-2">
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        width: 400,
                        height: 150,
                        className: "w-full h-full"
                      }}
                      dotSize={1}
                      minWidth={1.5}
                      maxWidth={2.5}
                      penColor="#2D2F5E"
                      onEnd={saveSignature}
                      clearOnResize={false}
                    />
                  </div>
                  <p className="text-xs text-[#2D2F5E] opacity-75 mt-1">Sign above with finger or stylus</p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-[#2D2F5E] px-4 py-3 rounded-lg font-medium transition"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const clientItems = Object.values(selectedDateItems).flat().filter(i => i.client_id === selectedClientId);
                      handleMarkPaid(selectedClientId, clientItems);
                    }}
                    disabled={saving || !signature || !paymentData.amount}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? "Saving..." : "✓ Confirm"}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedClientId(null)}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-[#2D2F5E] px-4 py-2 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delivery List */}
        {Object.keys(selectedDateItems).length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-[#2D2F5E] text-lg">📦 No deliveries for {selectedDate}</div>
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(selectedDateItems).map(([clientId, clientItems], index) => {
              const client = clients.find(c => c.id === parseInt(clientId));
              const totalDue = clientItems.reduce((sum, i) => sum + i.total_value_groszy, 0);
              const totalPaid = clientItems.reduce((sum, i) => sum + (i.payment_amount_groszy || 0), 0);
              const balance = totalDue - totalPaid;

              const status = totalPaid >= totalDue
                ? "Paid"
                : totalPaid > 0
                  ? "Partial"
                  : "Pending";

              const statusColor = status === "Paid"
                ? "bg-green-100 text-green-800 border-green-300"
                : status === "Partial"
                  ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                  : "bg-red-100 text-red-800 border-red-300";

              const isExpanded = expandedClient === clientId;

              return (
                <div key={clientId} className="bg-white rounded-xl shadow-lg border-2 border-[#2D2F5E] border-opacity-10 overflow-hidden">
                  {/* Card Header */}
                  <div
                    className="bg-gradient-to-r from-[#2D2F5E] to-[#3D3F6E] text-white p-4 cursor-pointer"
                    onClick={() => setExpandedClient(isExpanded ? null : clientId)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg font-bold">#{index + 1}</span>
                          <span className={`text-xs px-2 py-1 rounded border font-medium ${statusColor}`}>
                            {status}
                          </span>
                        </div>
                        <h3 className="font-bold text-lg">{client?.name || `Client ${clientId}`}</h3>
                        {client?.address && <p className="text-sm opacity-90 mt-1">📍 {client.address}</p>}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">{(balance / 100).toFixed(2)} PLN</div>
                        <div className="text-xs opacity-90">Balance</div>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Content */}
                  {isExpanded && (
                    <div className="p-4 bg-[#F5E9D8]">
                      {client?.telephone && (
                        <div className="mb-3 flex items-center gap-2">
                          <span className="text-[#2D2F5E] font-medium">📞</span>
                          <a href={`tel:${client.telephone}`} className="text-[#F76B4C] font-medium hover:underline">
                            {client.telephone}
                          </a>
                        </div>
                      )}

                      <div className="space-y-2 mb-4">
                        {clientItems.map((item) => (
                          <div key={item.id} className="bg-white p-3 rounded-lg flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <span className="bg-[#2D2F5E] text-white text-xs px-2 py-1 rounded font-bold">
                                {item.quantity}×
                              </span>
                              <span className="text-[#2D2F5E] font-medium">{item.item_name}</span>
                            </div>
                            <span className="text-[#2D2F5E] font-bold">{(item.total_value_groszy / 100).toFixed(2)} PLN</span>
                          </div>
                        ))}
                      </div>

                      <div className="bg-white p-3 rounded-lg mb-4 space-y-1">
                        <div className="flex justify-between text-sm text-[#2D2F5E]">
                          <span>Total Due:</span>
                          <span className="font-medium">{(totalDue / 100).toFixed(2)} PLN</span>
                        </div>
                        <div className="flex justify-between text-sm text-[#2D2F5E]">
                          <span>Paid:</span>
                          <span className="font-medium text-green-600">{(totalPaid / 100).toFixed(2)} PLN</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold text-[#2D2F5E] pt-2 border-t border-[#2D2F5E] border-opacity-20">
                          <span>Balance:</span>
                          <span className={balance > 0 ? "text-[#F76B4C]" : "text-green-600"}>
                            {(balance / 100).toFixed(2)} PLN
                          </span>
                        </div>
                      </div>

                      {balance > 0 && (
                        <button
                          className="w-full bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-3 rounded-lg font-bold text-lg transition shadow-lg"
                          onClick={() => setSelectedClientId(parseInt(clientId))}
                        >
                          💰 Record Payment
                        </button>
                      )}

                      {clientItems[0]?.signature && (
                        <div className="mt-4">
                          <p className="text-xs text-[#2D2F5E] font-medium mb-2">Customer Signature:</p>
                          <img src={clientItems[0].signature} alt="Signature" className="border-2 border-[#2D2F5E] border-opacity-20 rounded-lg bg-white p-2 max-h-24" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}